
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { LoginRequestModel } from '@/models/loginRequest';

@Component
export default class LoginRequestDialog extends Vue {
  @Prop({ required: true }) public value!: boolean;
  @Prop({ required: true }) public merchantId!: number;
  @Prop({ required: true }) public customerId!: string;
  @Prop({ default: '' }) public agentId!: string;
  @Prop({ default: '' }) public agentName!: string;
  @Prop({ default: '' }) public agentEmail!: string;
  @Prop({ default: null }) public currentRequest!: LoginRequestModel | null;

  public reason: string = '';
  public loading: boolean = false;
  public successMessage: string = '';
  public errorMessage: string = '';

  get show() {
    return this.value;
  }

  set show(value: boolean) {
    this.$emit('input', value);
  }

  get hasActiveRequest(): boolean {
    if (!this.currentRequest) {
      return false;
    }
    // Check if request is pending or approved and not expired
    const isActiveStatus = this.currentRequest.status === 'pending' || this.currentRequest.status === 'approved';
    const isNotExpired = !this.currentRequest.isExpired;
    return isActiveStatus && isNotExpired;
  }

  get activeRequest(): LoginRequestModel | null {
    return this.hasActiveRequest ? this.currentRequest : null;
  }

  @Watch('show')
  public async onShowChanged(newVal: boolean) {
    if (newVal) {
      // Reset form when dialog opens
      this.reason = '';
      this.successMessage = '';
      this.errorMessage = '';
    }
  }

  public async sendRequest() {
    this.loading = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      const request = new LoginRequestModel({
        merchantId: this.merchantId,
        customerId: this.customerId,
        agentId: this.agentId,
        agentName: this.agentName,
        agentEmail: this.agentEmail,
        reason: this.reason,
        accessDurationHours: 24,
      });

      await request.create();

      this.successMessage = 'Login request sent successfully!';
      this.$emit('request-created', request);

      // Close dialog after 2 seconds
      setTimeout(() => {
        this.close();
      }, 2000);
    } catch (error: any) {
      console.error('Error creating login request:', error);

      if (error.response?.status === 409) {
        const errorMsg = error.response?.data?.error || '';
        if (errorMsg.includes('approved')) {
          this.errorMessage = 'An approved login request is already active for this customer.';
        } else {
          this.errorMessage = 'A pending login request already exists for this customer.';
        }
      } else if (error.response?.data?.error) {
        this.errorMessage = error.response.data.error;
      } else {
        this.errorMessage = 'Failed to send login request. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  public close() {
    this.show = false;
    this.reason = '';
    this.successMessage = '';
    this.errorMessage = '';
  }

  public formatDate(dateString?: string): string {
    if (!dateString) { return 'N/A'; }
    const date = new Date(dateString);
    return date.toLocaleString();
  }
}
